import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { Button } from '@heroui/react';
import { faFileExcel, faFilePdf, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MainContainer } from '../../components';
import { autoTable } from "jspdf-autotable"

function GstRegister() {
    const location = useLocation();
    const navigate = useNavigate();
    const { compName, gstNo } = useSelector((state) => state.company);
    const [gstData, setGstData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { fromDate, toDate } = location.state || {};

    // Fetch data from API or location state
    useEffect(() => {
        if (location.state?.reportData) {
            setGstData(location.state.reportData[0] || []);
            setLoading(false);
        } else {
            fetchGstData();
        }
    }, [location.state]);

    const fetchGstData = async () => {
        try {
            setLoading(true);
            // Replace with your actual API endpoint
            const response = await fetch('your-api-endpoint-for-gst-register');
            const data = await response.json();

            if (data.status === 200) {
                setGstData(data.data[0] || []);
            } else {
                setError(data.message || 'Failed to fetch GST data');
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch GST data');
        } finally {
            setLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/');
    };

    // Format currency
    const formatCurrency = (num) => {
        if (num === null || num === undefined) return '0.00';
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Calculate totals
    const totals = gstData.reduce((acc, item) => ({
        NetAmount: acc.NetAmount + (item.NetAmount || 0),
        GrossAmount: acc.GrossAmount + (item.GrossAmt || 0),
        DiscAmount: acc.DiscAmount + (item.DiscAmount || 0),
        TaxableValue: acc.TaxableValue + (item.TaxableValue || 0),
        CGST: acc.CGST + (item.CGST || 0),
        SGST: acc.SGST + (item.SGST || 0),
        IGST: acc.IGST + (item.IGST || 0),
        TotalTax: acc.TotalTax + (item.TotalTax || 0)
    }), {
        NetAmount: 0,
        GrossAmount: 0,
        DiscAmount: 0,
        TaxableValue: 0,
        CGST: 0,
        SGST: 0,
        IGST: 0,
        TotalTax: 0
    });

    // Export to PDF
    const exportToPDF = () => {
        if (gstData.length === 0) return;

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm'
        });

        // Header
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(compName, 14, 10);
        doc.text(`GST No: ${gstNo}`, 14, 15);
        doc.text('Report: GST Register', 14, 20);
        if (fromDate && toDate) {
            doc.text(`From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
                doc.internal.pageSize.width - 14, 20, { align: 'right' });
        }

        // Prepare data for the table
        const headers = [
            'Date',
            'Bill No',
            'Voucher No',
            'Party Name',
            'GST No',
            'Place',
            'Net Amount',
            'Gross Amount',
            'Disc. Amount',
            'Taxable Value',
            'CGST',
            'SGST',
            'IGST',
            'Total Tax'
        ];

        const body = gstData.map(item => [
            formatDate(item.Bill_Date),
            item.Bill_No,
            item.Vou_No,
            item.PartyName,
            item.GST_No,
            item.Place,
            formatCurrency(item.NetAmount),
            formatCurrency(item.GrossAmt),
            formatCurrency(item.DiscAmount),
            formatCurrency(item.TaxableValue),
            formatCurrency(item.CGST),
            formatCurrency(item.SGST),
            formatCurrency(item.IGST),
            formatCurrency(item.TotalTax)
        ]);

        // Add totals row
        body.push([
            { content: 'TOTAL', colSpan: 6, styles: { fontStyle: 'bold', halign: 'Left' } },
            formatCurrency(totals.NetAmount),
            formatCurrency(totals.GrossAmount),
            formatCurrency(totals.DiscAmount),
            formatCurrency(totals.TaxableValue),
            formatCurrency(totals.CGST),
            formatCurrency(totals.SGST),
            formatCurrency(totals.IGST),
            formatCurrency(totals.TotalTax)
        ]);

        // Generate PDF
        autoTable(doc,{
            startY: 25,
            margin: { top: 25, left: 5, right: 5, bottom: 15 },
            head: [headers],
            body: body,
            theme: 'grid',
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 20 },  // Date
                1: { cellWidth: 20 },  // Bill No
                2: { cellWidth: 20 },  // Voucher No
                3: { cellWidth: 25 },  // Party Name
                4: { cellWidth: 20 },  // GST No
                5: { cellWidth: 20 },  // Place
                6: { cellWidth: 23, halign: 'right' },  // Net Amount
                7: { cellWidth: 23, halign: 'right' },  // Gross Amount
                8: { cellWidth: 23, halign: 'right' },  // Disc. Amount
                9: { cellWidth: 23, halign: 'right' },  // Taxable Value
                10: { cellWidth: 17, halign: 'right' },  // CGST
                11: { cellWidth: 17, halign: 'right' },  // SGST
                12: { cellWidth: 17, halign: 'right' },  // IGST
                13: { cellWidth: 23, halign: 'right' }  // Total Tax
            },
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak'
            },
            didDrawPage: function (data) {

                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.text(compName, 14, 10);
                doc.text(`GST No: ${gstNo}`, 14, 15);
                doc.text('Report: GST Register', 14, 20);
                doc.text(`From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
                    doc.internal.pageSize.width - 14, 20, { align: 'right' });

                // Footer
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: 'center' }
                );
            }
        });

        doc.save('GST_Register.pdf');
    };

    // Export to Excel
    const exportToExcel = () => {
        if (gstData.length === 0) return;

        // Prepare worksheet data
        const wsData = [
            ['Date', 'Bill No', 'Voucher No', 'Party Name', 'GST No', 'Place', 'Net Amount', 'Gross Amount', 'Disc. Amount', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Tax'],
            ...gstData.map(item => [
                formatDate(item.Bill_Date),
                item.Bill_No,
                item.Vou_No,
                item.PartyName,
                item.GST_No,
                item.Place,
                item.NetAmount,
                item.GrossAmt,
                item.DiscAmount,
                item.TaxableValue,
                item.CGST,
                item.SGST,
                item.IGST,
                item.TotalTax
            ]),
            ['TOTAL', '', '', '', '', '',
                totals.NetAmount,
                totals.GrossAmt,
                totals.DiscAmount,
                totals.TaxableValue,
                totals.CGST,
                totals.SGST,
                totals.IGST,
                totals.TotalTax]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "GST Register");

        // Auto-size columns
        const wscols = [
            { wch: 12 }, // Date
            { wch: 12 }, // Bill No
            { wch: 12 }, // Voucher No
            { wch: 25 }, // Party Name
            { wch: 18 }, // GST No
            { wch: 12 }, // Place
            { wch: 12 }, // Net Amount
            { wch: 12 }, // Gross Amount
            { wch: 12 }, // Disc. Amount
            { wch: 12 }, // Taxable Value
            { wch: 10 }, // CGST
            { wch: 10 }, // SGST
            { wch: 10 }, // IGST
            { wch: 12 }  // Total Tax
        ];
        ws['!cols'] = wscols;

        // Format totals row
        const totalsRow = wsData.length - 1;
        for (let i = 0; i < 14; i++) {
            const cellRef = XLSX.utils.encode_cell({ r: totalsRow, c: i });
            if (!ws[cellRef]) ws[cellRef] = {};
            ws[cellRef].s = { font: { bold: true } };
            if (i >= 6) {
                ws[cellRef].t = 'n';
                ws[cellRef].z = '#,##0.00';
            }
        }

        XLSX.writeFile(wb, 'GST_Register.xlsx');
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
                <div>Loading GST Register data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
                <div className="text-red-500">Error: {error}</div>
            </div>
        );
    }

    return (
        <MainContainer
            pageName="GST RTegister"
            breadcrumbs={["Reports", "GST Reports", "GST Register"]}
        >
            <div className="min-h-screen bg-gray-50 p-0">
                <div className="max-w-auto mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h1 className="text-md font-semibold text-lime-800">GST Register</h1>
                                <p className="text-sm text-pink-600">
                                    {fromDate && toDate ? (
                                        `From ${formatDate(fromDate)} To ${formatDate(toDate)}`
                                    ) : 'All transactions'}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    onPress={exportToPDF}
                                    size="sm"
                                    color="danger"
                                    startContent={<FontAwesomeIcon icon={faFilePdf} className="h-4 w-4" />}
                                    isDisabled={gstData.length === 0}
                                >
                                    PDF
                                </Button>
                                <Button
                                    onPress={exportToExcel}
                                    size="sm"
                                    color="success"
                                    startContent={<FontAwesomeIcon icon={faFileExcel} className="h-4 w-4" />}
                                    isDisabled={gstData.length === 0}
                                >
                                    Excel
                                </Button>
                                <Button
                                    onPress={handleGoBack}
                                    size="sm"
                                    color='primary'
                                    variant="bordered"
                                    startContent={<FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />}
                                >
                                    Back
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        {gstData.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">No GST data available</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill No</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voucher No</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST No</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Place</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net Amount</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Amount</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Disc. Amount</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Taxable Value</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CGST</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">SGST</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">IGST</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tax</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {gstData.map((item, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{formatDate(item.Bill_Date)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{item.Bill_No}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{item.Vou_No}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{item.PartyName}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{item.GST_No}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{item.Place}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.NetAmount)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.GrossAmt)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.DiscAmount)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.TaxableValue)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.CGST)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.SGST)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.IGST)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(item.TotalTax)}</td>
                                            </tr>
                                        ))}

                                        {/* Totals row */}
                                        <tr className="bg-gray-100 font-medium">
                                            <td colSpan="6" className="px-4 py-2 text-sm text-gray-700 text-Left">TOTAL</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.NetAmount)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.GrossAmt)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.DiscAmount)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.TaxableValue)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.CGST)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.SGST)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.IGST)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(totals.TotalTax)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainContainer>
    );
}

export default GstRegister;